import { useState } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wrench, Plus, Search, Edit, Trash2, Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const KIND_LABELS: Record<string, string> = { tool: "Herramienta", holder: "Porta-herramientas" };

function ToolDialog({ open, onOpenChange, tool, onSaved }: any) {
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState<any>(null);
  // inicializar al abrir
  if (open && f === null) {
    setF({
      kind: tool?.kind || "tool", code: tool?.code || "", name: tool?.name || "",
      tool_offset_mm: tool?.tool_offset_mm ?? "", diameter_mm: tool?.diameter_mm ?? "",
      flutes: tool?.flutes ?? "", holder_type: tool?.holder_type || "", notes: tool?.notes || "",
      current_stock: tool?.current_stock ?? 0, min_stock: tool?.min_stock ?? 0,
      is_consumable: tool?.is_consumable !== false,
    });
  }
  const close = () => { setF(null); onOpenChange(false); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setLoading(true);
    try {
      const payload: any = {
        kind: f.kind, code: f.code || null, name: f.name.trim(),
        tool_offset_mm: f.tool_offset_mm === "" ? null : parseFloat(f.tool_offset_mm),
        diameter_mm: f.diameter_mm === "" ? null : parseFloat(f.diameter_mm),
        flutes: f.flutes === "" ? null : parseInt(f.flutes),
        holder_type: f.holder_type || null, notes: f.notes || null,
        current_stock: parseInt(f.current_stock) || 0,
        min_stock: parseInt(f.min_stock) || 0,
        is_consumable: f.is_consumable,
      };
      if (tool) {
        const { error } = await api.from("machining_tools").update(payload).eq("id", tool.id);
        if (error) throw error;
        toast.success("Actualizado");
      } else {
        const { error } = await api.from("machining_tools").insert([payload]);
        if (error) throw error;
        toast.success("Creado");
      }
      close();
      onSaved?.();
    } catch (err: any) { toast.error(err.message || "No se pudo guardar"); }
    finally { setLoading(false); }
  };

  if (!open || !f) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{tool ? "Editar" : "Nueva"} herramienta / porta</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tool">Herramienta</SelectItem>
                  <SelectItem value="holder">Porta-herramientas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Código</Label>
              <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="Ej: MGM_RoundMill_10" />
            </div>
          </div>
          <div>
            <Label>Nombre *</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ej: Fresa esférica 10mm" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Tool offset (mm)</Label>
              <Input type="number" step="0.01" value={f.tool_offset_mm} onChange={(e) => setF({ ...f, tool_offset_mm: e.target.value })} />
            </div>
            <div>
              <Label>Diámetro (mm)</Label>
              <Input type="number" step="0.01" value={f.diameter_mm} onChange={(e) => setF({ ...f, diameter_mm: e.target.value })} />
            </div>
            <div>
              <Label>Nº filos</Label>
              <Input type="number" value={f.flutes} onChange={(e) => setF({ ...f, flutes: e.target.value })} />
            </div>
          </div>
          {f.kind === "holder" && (
            <div>
              <Label>Tipo de porta</Label>
              <Input value={f.holder_type} onChange={(e) => setF({ ...f, holder_type: e.target.value })} placeholder="Ej: HSK63, BT40..." />
            </div>
          )}
          <div>
            <Label>Notas</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Stock actual</Label>
              <Input type="number" value={f.current_stock} onChange={(e) => setF({ ...f, current_stock: e.target.value })} min={0} />
            </div>
            <div>
              <Label>Stock mínimo</Label>
              <Input type="number" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} min={0} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={f.is_consumable} onChange={(e) => setF({ ...f, is_consumable: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm">Consumible</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancelar</Button>
            <Button type="submit" disabled={loading}>{loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{tool ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MachiningTools() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editTool, setEditTool] = useState<any>(null);

  const { data: tools } = useQuery({
    queryKey: ["machining-tools"],
    queryFn: async () => (await api.from("machining_tools").select("*").eq("is_active", true).order("name")).data || [],
  });

  const filtered = (tools || []).filter((t: any) => {
    if (kindFilter !== "all" && t.kind !== kindFilter) return false;
    if (search && !`${t.name} ${t.code || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["machining-tools"] });

  const remove = async (t: any) => {
    if (!window.confirm(`¿Eliminar "${t.name}"?`)) return;
    const { error } = await api.from("machining_tools").update({ is_active: false }).eq("id", t.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Eliminado");
    refresh();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Wrench className="w-6 h-6" />Biblioteca CNC</h1>
            <p className="text-muted-foreground text-sm">Herramientas y porta-herramientas para órdenes de mecanizado</p>
          </div>
          <Button onClick={() => { setEditTool(null); setDlgOpen(true); }}><Plus className="w-4 h-4 mr-1" />Nueva</Button>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <CardTitle className="text-base">Catálogo ({filtered.length})</CardTitle>
              <div className="flex items-center gap-2">
                <Tabs value={kindFilter} onValueChange={setKindFilter}>
                  <TabsList>
                    <TabsTrigger value="all">Todas</TabsTrigger>
                    <TabsTrigger value="tool">Herramientas</TabsTrigger>
                    <TabsTrigger value="holder">Porta</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="pl-8 h-9 w-48" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Sin elementos.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2">Tipo</th><th className="p-2">Código</th><th className="p-2">Nombre</th>
                      <th className="p-2">Stock</th><th className="p-2">Mín</th>
                      <th className="p-2">Tool offset (mm)</th><th className="p-2">Ø (mm)</th><th className="p-2">Filos</th>
                      <th className="p-2">Porta</th><th className="p-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t: any) => (
                      <tr key={t.id} className="border-t">
                        <td className="p-2"><Badge variant="outline">{KIND_LABELS[t.kind] || t.kind}</Badge></td>
                        <td className="p-2 font-mono text-xs">{t.code || "—"}</td>
                        <td className="p-2">{t.name}</td>
                        <td className={"p-2 " + (t.is_consumable && t.min_stock > 0 && t.current_stock <= t.min_stock ? "text-amber-600 font-bold" : "")}>{t.current_stock ?? 0}</td>
                        <td className="p-2 text-muted-foreground">{t.min_stock ?? 0}</td>
                        <td className="p-2">{t.tool_offset_mm ?? "—"}</td>
                        <td className="p-2">{t.diameter_mm ?? "—"}</td>
                        <td className="p-2">{t.flutes ?? "—"}</td>
                        <td className="p-2">{t.holder_type || "—"}</td>
                        <td className="p-2 text-right">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditTool(t); setDlgOpen(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(t)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ToolDialog open={dlgOpen} onOpenChange={setDlgOpen} tool={editTool} onSaved={refresh} />
    </DashboardLayout>
  );
}
